import { useState, useEffect } from "react";
import { Database, Cog, FileBarChart, Target, BarChart3, Plus, Minus, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SBTIEmissionsChart from "./SBTIEmissionsChart";
import DataPipelineAnimation from "./DataPipelineAnimation";
import ScreenshotCarouselAnimation from "./ScreenshotCarouselAnimation";
import CarbonInitiativeTimeline from "./CarbonInitiativeTimeline";
import generateReportGif from "@/assets/generate-report.gif";

// Preload the GIF on module load for instant display
const preloadImage = new Image();
preloadImage.src = generateReportGif;

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const [expandedStep, setExpandedStep] = useState<number>(0);
  const [carouselTab, setCarouselTab] = useState<number>(0);
  
  const carouselTabs = [
    { id: "overview", label: "Overview" },
    { id: "map", label: "Emissions Map" },
    { id: "flow", label: "Emissions Flow" },
  ];
  
  const steps = [
    {
      number: "01",
      icon: Database,
      title: t("howItWorks.step1Title"),
      description: t("howItWorks.step1Desc"),
      image: null,
      customComponent: "pipeline"
    },
    {
      number: "02",
      icon: Cog,
      title: t("howItWorks.step2Title"),
      description: t("howItWorks.step2Desc"),
      image: null,
      customComponent: "screenshots"
    },
    {
      number: "03",
      icon: FileBarChart,
      title: t("howItWorks.step3Title"),
      description: t("howItWorks.step3Desc"),
      image: generateReportGif
    },
    {
      number: "04",
      icon: Target,
      title: t("howItWorks.step4Title"),
      description: t("howItWorks.step4Desc"),
      image: null,
      customComponent: "chart"
    },
    {
      number: "05",
      icon: BarChart3,
      title: t("howItWorks.step5Title"),
      description: t("howItWorks.step5Desc"),
      image: null,
      customComponent: "timeline"
    }
  ];
  
  const handleStepClick = (index: number) => {
    setExpandedStep(index);
  };

  return (
    <section id="how-it-works" className="py-8 lg:py-12 px-2">
      <div className="bg-dark rounded-[31px] relative overflow-hidden py-10 lg:py-16 px-6 sm:px-8 lg:px-12">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--dark-foreground)) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto max-w-7xl relative z-10">
            {/* Centered Section Header */}
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-dark-foreground mb-4">
                {t("howItWorks.title1")}
                <span className="text-accent"> {t("howItWorks.title2")}</span>
              </h2>
              <p className="text-lg text-dark-foreground/70 max-w-2xl mx-auto">
                {t("howItWorks.description")}
              </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left Column - Accordion */}
              <div>
                <div className="space-y-0">
                  {steps.map((step, index) => (
                    <div key={step.number} className="border-t border-dark-foreground/10 last:border-b">
                      <button
                        onClick={() => handleStepClick(index)}
                        className="w-full flex items-center justify-between py-4 sm:py-5 text-left group transition-colors duration-300 hover:bg-dark-foreground/5"
                      >
                        <div className="flex items-center gap-2 sm:gap-4 pr-2">
                          <span className={`text-sm sm:text-lg font-bold transition-colors duration-300 ${expandedStep === index ? 'text-accent' : 'text-dark-foreground/40'}`}>
                            {step.number}
                          </span>
                          <h3 className={`text-sm sm:text-lg lg:text-xl font-medium transition-colors duration-300 ${expandedStep === index ? 'text-dark-foreground' : 'text-dark-foreground/70 group-hover:text-dark-foreground'}`}>
                            {step.title}
                          </h3>
                        </div>
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${expandedStep === index ? 'text-accent' : 'text-accent group-hover:bg-accent/10'}`}>
                          {expandedStep === index ? <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                        </div>
                      </button>
                      
                      {/* Expanded Content */}
                      <div className={`overflow-hidden transition-all duration-500 ease-out ${expandedStep === index ? 'max-h-60 sm:max-h-40 opacity-100 pb-4 sm:pb-5' : 'max-h-0 opacity-0'}`}>
                        <p className="text-dark-foreground/60 leading-relaxed pl-6 sm:pl-10 text-sm sm:text-base pr-2">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Image Display */}
              <div className="relative lg:sticky lg:top-24 overflow-hidden scrollbar-hide rounded-2xl">
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 via-accent/5 to-transparent rounded-3xl blur-2xl opacity-60" />
                
                {/* Image Container */}
                <div className="relative overflow-hidden min-h-[280px] sm:min-h-0 aspect-[16/9] rounded-2xl">
                  {expandedStep >= 0 && expandedStep < steps.length && (
                    steps[expandedStep].customComponent === "chart" ? (
                      <div key={expandedStep} className="w-full h-full animate-fade-in">
                        <SBTIEmissionsChart />
                      </div>
                    ) : steps[expandedStep].customComponent === "pipeline" ? (
                      <div key={expandedStep} className="w-full h-full min-h-[280px] sm:min-h-0 animate-fade-in">
                        <DataPipelineAnimation />
                      </div>
                    ) : steps[expandedStep].customComponent === "screenshots" ? (
                      <div key={expandedStep} className="w-full h-full animate-fade-in">
                        <ScreenshotCarouselAnimation onTabChange={setCarouselTab} />
                      </div>
                    ) : steps[expandedStep].customComponent === "timeline" ? (
                      <div key={expandedStep} className="w-full h-[436px] max-w-[636px] mx-auto animate-fade-in">
                        <CarbonInitiativeTimeline />
                      </div>
                    ) : (
                      <img
                        key={expandedStep}
                        src={steps[expandedStep].image}
                        alt={`Carbonwize ${steps[expandedStep].title} - Step ${steps[expandedStep].number} of carbon management workflow`}
                        loading="eager"
                        className="w-full h-full animate-fade-in object-contain rounded-2xl"
                      />
                    )
                  )}
                  
                  {/* Overlay with step info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 pointer-events-none">
                    {expandedStep >= 0 && expandedStep < steps.length && (
                      <div className="animate-fade-in">
                        <span className="inline-flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-accent/20 border border-accent/30 mb-2 sm:mb-3">
                          <span className="text-[10px] sm:text-xs font-medium text-accent">Step {steps[expandedStep].number}</span>
                        </span>
                        <p className="text-base sm:text-xl font-bold text-white">
                          {steps[expandedStep].title}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Standards alignment - shown below image for Generate Reports step */}
                {expandedStep === 2 && (
                  <div className="mt-4 flex flex-col items-center gap-2 animate-fade-in my-[8px]">
                    <span className="text-dark-foreground/60 text-sm font-medium">{t("howItWorks.standardsAlignment")}</span>
                    <img src="/lovable-uploads/8d56f73e-d357-4ec7-bd6a-9832088bff37.png" alt="GHG Protocol, ISO 14064, TGO CFO standards logos - Carbonwize certified carbon reporting standards alignment" className="h-12 object-contain" />
                  </div>
                )}
                
                {/* Dot Indicators for screenshots - below the image */}
                {expandedStep === 1 && (
                  <div className="flex gap-3 justify-center mt-4">
                    {carouselTabs.map((tab, index) => (
                      <button
                        key={tab.id}
                        onClick={() => setCarouselTab(index)}
                        aria-label={tab.label}
                        className={`h-3 rounded-full transition-all duration-300 ${
                          carouselTab === index
                            ? "bg-accent w-8"
                            : "bg-dark-foreground/40 w-3 hover:bg-dark-foreground/60"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mini CTA */}
            <div className="mt-16 text-center animate-fade-up">
              <p className="text-dark-foreground/70 mb-4 text-lg">
                {t("howItWorks.ctaText")}
              </p>
              <Link to="/contact-us">
                <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 group">
                  {t("howItWorks.ctaButton")}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
